import { updateObject } from "../../helpers/updateObject";
import {
  // Existing
  CREATE_ORDER_FAILURE,
  CREATE_ORDER_REQUEST,
  CREATE_ORDER_SUCCESS,
  FETCH_ATTRIBUTES_FAILURE,
  FETCH_ATTRIBUTES_REQUEST,
  FETCH_ATTRIBUTES_SUCCESS,
  GET_ORDER_DETAILS_FAILURE,
  GET_ORDER_DETAILS_REQUEST,
  GET_ORDER_DETAILS_SUCCESS,
  GET_ORDERS,
  // New
  GET_HANDYMAN_PROPOSALS_REQUEST,
  GET_HANDYMAN_PROPOSALS_SUCCESS,
  GET_HANDYMAN_PROPOSALS_FAILURE,
  GET_HANDYMAN_AVAILABLE_ORDERS_REQUEST,
  GET_HANDYMAN_AVAILABLE_ORDERS_SUCCESS,
  GET_HANDYMAN_AVAILABLE_ORDERS_FAILURE,
  UPDATE_HANDYMAN_PROPOSAL_REQUEST,
  UPDATE_HANDYMAN_PROPOSAL_SUCCESS,
  UPDATE_HANDYMAN_PROPOSAL_FAILURE,
  SEND_HANDYMAN_PROPOSAL_REQUEST,
  SEND_HANDYMAN_PROPOSAL_SUCCESS,
  SEND_HANDYMAN_PROPOSAL_FAILURE,
  GET_ORDER_PROPOSALS_REQUEST,
  GET_ORDER_PROPOSALS_SUCCESS,
  GET_ORDER_PROPOSALS_FAILURE,
  UPDATE_ORDER_PROPOSAL_REQUEST,
  UPDATE_ORDER_PROPOSAL_SUCCESS,
  UPDATE_ORDER_PROPOSAL_FAILURE,
  SEND_ORDER_PROPOSAL_REQUEST,
  SEND_ORDER_PROPOSAL_SUCCESS,
  SEND_ORDER_PROPOSAL_FAILURE,
  CREATE_OR_GET_CHAT_FAILURE,
  CREATE_OR_GET_CHAT_REQUEST,
  CREATE_OR_GET_CHAT_SUCCESS,
  MARK_ORDER_AS_DONE_REQUEST,
  COMPLETE_ORDER_FAILURE,
  COMPLETE_ORDER_REQUEST,
  COMPLETE_ORDER_SUCCESS,
  MARK_ORDER_AS_DONE_FAILURE,
  MARK_ORDER_AS_DONE_SUCCESS,
} from "./actions";
import {
  CategoryAttributeViewModel,
  OrderViewModel,
  OrderProposalViewModel,
} from "../../configuration/api/apiSchema";

export type OrderState = {
  isLoading: boolean;
  isError: boolean;
  orders: OrderViewModel[];
  attributes: CategoryAttributeViewModel[] | null;
  isFetchingAttributes: boolean;
  fetchAttributesError: string | null;
  createOrderLoading: boolean;
  createOrderError: string | null;
  createdOrder: OrderViewModel | null;
  orderDetails: OrderViewModel | null;
  orderDetailsLoading: boolean;
  orderDetailsError: string | null;

  // NEW state slices
  handymanProposals: OrderProposalViewModel[];
  handymanAvailableOrders: OrderViewModel[];
  handymanProposalsLoading: boolean;
  handymanProposalsError: string | null;
  handymanAvailableOrdersLoading: boolean;
  handymanAvailableOrdersError: string | null;
  updateHandymanProposalLoading: boolean;
  updateHandymanProposalError: string | null;
  sendHandymanProposalLoading: boolean;
  sendHandymanProposalError: string | null;

  orderProposals: OrderProposalViewModel[];
  orderProposalsLoading: boolean;
  orderProposalsError: string | null;
  updateOrderProposalLoading: boolean;
  updateOrderProposalError: string | null;
  sendOrderProposalLoading: boolean;
  sendOrderProposalError: string | null;
  createOrGetChatLoading: boolean;
  createOrGetChatError: string | null;
};

const initialState: OrderState = {
  isLoading: false,
  isError: false,
  orders: [],
  attributes: null,
  isFetchingAttributes: false,
  fetchAttributesError: null,
  createOrderLoading: false,
  createOrderError: null,
  createdOrder: null,
  orderDetails: null,
  orderDetailsLoading: false,
  orderDetailsError: null,

  // NEW
  handymanProposals: [],
  handymanAvailableOrders: [],
  handymanProposalsLoading: false,
  handymanProposalsError: null,
  handymanAvailableOrdersLoading: false,
  handymanAvailableOrdersError: null,
  updateHandymanProposalLoading: false,
  updateHandymanProposalError: null,
  sendHandymanProposalLoading: false,
  sendHandymanProposalError: null,

  orderProposals: [],
  orderProposalsLoading: false,
  orderProposalsError: null,
  updateOrderProposalLoading: false,
  updateOrderProposalError: null,
  sendOrderProposalLoading: false,
  sendOrderProposalError: null,
  createOrGetChatLoading: false,
  createOrGetChatError: null,
};

export const reducer = (state: OrderState = initialState, action: any) => {
  switch (action.type) {
    // -------------------------
    // Existing Cases
    // -------------------------
    case GET_ORDERS.REQUEST:
      return updateObject(state, { isLoading: true, isError: false, orders: [] });
    case GET_ORDERS.SUCCESS:
      return updateObject(state, { isLoading: false, orders: action.orders });

    case FETCH_ATTRIBUTES_REQUEST:
      return { ...state, isFetchingAttributes: true, fetchAttributesError: null, attributes: null };
    case FETCH_ATTRIBUTES_SUCCESS:
      return { ...state, isFetchingAttributes: false, attributes: action.payload };
    case FETCH_ATTRIBUTES_FAILURE:
      return { ...state, isFetchingAttributes: false, fetchAttributesError: action.payload };

    case CREATE_ORDER_REQUEST:
      return { ...state, createOrderLoading: true, createOrderError: null, createdOrder: null };
    case CREATE_ORDER_SUCCESS:
      return { ...state, createOrderLoading: false, createdOrder: action.payload };
    case CREATE_ORDER_FAILURE:
      return { ...state, createOrderLoading: false, createOrderError: action.payload };

    case GET_ORDER_DETAILS_REQUEST:
      return { ...state, orderDetailsLoading: true, orderDetailsError: null };
    case GET_ORDER_DETAILS_SUCCESS:
      return { ...state, orderDetailsLoading: false, orderDetails: action.payload };
    case GET_ORDER_DETAILS_FAILURE:
      return { ...state, orderDetailsLoading: false, orderDetailsError: action.payload };

    // ---------------------------------------------
    // NEW: Handyman - Proposals & Available Orders
    // ---------------------------------------------
    case GET_HANDYMAN_PROPOSALS_REQUEST:
      return { ...state, handymanProposalsLoading: true, handymanProposalsError: null };
    case GET_HANDYMAN_PROPOSALS_SUCCESS:
      return { ...state, handymanProposalsLoading: false, handymanProposals: action.payload };
    case GET_HANDYMAN_PROPOSALS_FAILURE:
      return { ...state, handymanProposalsLoading: false, handymanProposalsError: action.payload };

    case GET_HANDYMAN_AVAILABLE_ORDERS_REQUEST:
      return {
        ...state,
        handymanAvailableOrdersLoading: true,
        handymanAvailableOrdersError: null,
      };
    case GET_HANDYMAN_AVAILABLE_ORDERS_SUCCESS:
      return {
        ...state,
        handymanAvailableOrdersLoading: false,
        handymanAvailableOrders: action.payload,
      };
    case GET_HANDYMAN_AVAILABLE_ORDERS_FAILURE:
      return {
        ...state,
        handymanAvailableOrdersLoading: false,
        handymanAvailableOrdersError: action.payload,
      };

    case UPDATE_HANDYMAN_PROPOSAL_REQUEST:
      return { ...state, updateHandymanProposalLoading: true, updateHandymanProposalError: null };
    case UPDATE_HANDYMAN_PROPOSAL_SUCCESS: {
      // You could update the specific proposal item in handymanProposals array
      const updatedProposal: OrderProposalViewModel = action.payload;
      const updatedProposals = state.handymanProposals.map((p) =>
        p.id === updatedProposal.id ? updatedProposal : p
      );
      return {
        ...state,
        updateHandymanProposalLoading: false,
        handymanProposals: updatedProposals,
      };
    }
    case UPDATE_HANDYMAN_PROPOSAL_FAILURE:
      return {
        ...state,
        updateHandymanProposalLoading: false,
        updateHandymanProposalError: action.payload,
      };

    case SEND_HANDYMAN_PROPOSAL_REQUEST:
      return { ...state, sendHandymanProposalLoading: true, sendHandymanProposalError: null };
    case SEND_HANDYMAN_PROPOSAL_SUCCESS:
      return { ...state, sendHandymanProposalLoading: false };
    case SEND_HANDYMAN_PROPOSAL_FAILURE:
      return {
        ...state,
        sendHandymanProposalLoading: false,
        sendHandymanProposalError: action.payload,
      };

    // ---------------------------------------------
    // NEW: Customer - Order Proposals
    // ---------------------------------------------
    case GET_ORDER_PROPOSALS_REQUEST:
      return { ...state, orderProposalsLoading: true, orderProposalsError: null };
    case GET_ORDER_PROPOSALS_SUCCESS:
      return { ...state, orderProposalsLoading: false, orderProposals: action.payload };
    case GET_ORDER_PROPOSALS_FAILURE:
      return { ...state, orderProposalsLoading: false, orderProposalsError: action.payload };

    case UPDATE_ORDER_PROPOSAL_REQUEST:
      return { ...state, updateOrderProposalLoading: true, updateOrderProposalError: null };
    case UPDATE_ORDER_PROPOSAL_SUCCESS: {
      const updatedProposal: OrderProposalViewModel = action.payload;
      const updatedProposals = state.orderProposals.map((p) =>
        p.id === updatedProposal.id ? updatedProposal : p
      );
      return {
        ...state,
        updateOrderProposalLoading: false,
        orderProposals: updatedProposals,
      };
    }
    case UPDATE_ORDER_PROPOSAL_FAILURE:
      return {
        ...state,
        updateOrderProposalLoading: false,
        updateOrderProposalError: action.payload,
      };

    case SEND_ORDER_PROPOSAL_REQUEST:
      return {
        ...state,
        sendOrderProposalLoading: true,
        sendOrderProposalError: null,
      };
    case SEND_ORDER_PROPOSAL_SUCCESS:
      return {
        ...state,
        sendOrderProposalLoading: false,
        sendOrderProposalError: null,
      };
    case SEND_ORDER_PROPOSAL_FAILURE:
      return {
        ...state,
        sendOrderProposalLoading: false,
        sendOrderProposalError: action.payload,
      };

    case CREATE_OR_GET_CHAT_REQUEST:
      return {
        ...state,
        createOrGetChatLoading: true,
        createOrGetChatError: null,
      };
    case CREATE_OR_GET_CHAT_SUCCESS:
      return {
        ...state,
        createOrGetChatLoading: false,
        // If you want to store the last chat object, you can do so here
        // e.g. lastOpenedChat: action.payload,
      };
    case CREATE_OR_GET_CHAT_FAILURE:
      return {
        ...state,
        createOrGetChatLoading: false,
        createOrGetChatError: action.payload,
      };

    case MARK_ORDER_AS_DONE_REQUEST:
      return { ...state, markAsDoneLoading: true, markAsDoneError: null };
    case MARK_ORDER_AS_DONE_SUCCESS:
      return { ...state, markAsDoneLoading: false, orderDetails: action.payload };
    case MARK_ORDER_AS_DONE_FAILURE:
      return { ...state, markAsDoneLoading: false, markAsDoneError: action.payload };

    case COMPLETE_ORDER_REQUEST:
      return { ...state, completeOrderLoading: true, completeOrderError: null };
    case COMPLETE_ORDER_SUCCESS:
      return { ...state, completeOrderLoading: false, orderDetails: action.payload };
    case COMPLETE_ORDER_FAILURE:
      return { ...state, completeOrderLoading: false, completeOrderError: action.payload };

    default:
      return state;
  }
};
